import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import AgGridReact from '../../common/AgGrid/AgGridReact.jsx';
import { gridTheme } from '../../common/AgGrid/gridTheme.js';
import { useSelectionColumnClick } from '../../datagrid/hooks/useSelectionColumnClick.js';

const PrefillSourceAgGrouped = forwardRef(({sourceData}, ref) =>  {
    const gridApiRef = React.useRef(null);

    useImperativeHandle(ref, () => ({
        getSelectedRows: () => {
            const selectedNodes = gridApiRef.current.getSelectedNodes();
            return selectedNodes.map(node => node.data);
        }
    }));

    const onGridReady = params => {
        gridApiRef.current = params.api;
        // Group by programme_form_link_id and find the latest 'created_at' for each group
        const latestEntries = sourceData.reduce((acc, curr) => {
            if (!acc[curr.programme_form_link_id] ||
                new Date(acc[curr.programme_form_link_id].created_at) < new Date(curr.created_at)
            ) {
                acc[curr.programme_form_link_id] = curr;
            }
            return acc;
        }, {});

        // Extract the IDs of the latest entries
        const idsToSelect = Object.values(latestEntries).map(entry => entry.id);
        params.api.forEachNode(node => {
            if (node.data && idsToSelect.includes(node.data.id)) {
                node.setSelected(true);
            }
        });
    };

    const onRowSelected = event => {
        // Check if the row is selected
        if (event.node.selected) {
            const selectedGroupId = event.node.data.programme_form_link_id;
    
            gridApiRef.current.forEachNode(node => {
                // Check if the node belongs to the same group and is not the currently selected node
                if (node.data 
                    && node.data.programme_form_link_id === selectedGroupId 
                    && node.id !== event.node.id
                    && node.selected
                ) {
                    node.setSelected(false);
                }
            });
        }
    };

    const isRowSelectable = rowNode => {
        // If it's a group row, it's not selectable
        return !rowNode.group;
    };
    
    

    const columnDefs = [
        {
            headerName: 'Submission type',
            valueGetter: function(params) {
                return params.data && params.data.name['en'] + ' - ' + params.data.form_name['en'];
            },
            rowGroup: true,
            hide: true,
        },
        {
            headerName: 'Submission entry',
            valueGetter: function(params) {
                return params.data && params.data.name['en'] + ' ' + dayjs(params.data.created_at).local().format('YYYY-MM-DD HH:mm Z');
            },
            flex: 1
        }
    ];

    const defaultColDef = {
        resizable: true,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true
    };

    const autoGroupColumnDef = {
        headerName: 'Submission type',
        cellRendererParams: { suppressCount: true },
    };

    const rowSelection = useMemo(() => ({
        mode: 'multiRow',
        groupSelects: "filteredDescendants",
        enableMultiSelectWithClick: true,
        enableClickSelection: false,
        selectAll: "filtered",
        isRowSelectable: isRowSelectable
    }), []);

    return (
        <div className="grid-theme" style={{ height: 400, width: '100%' }}>
            <AgGridReact theme={gridTheme}
                className="selection-grid"
                rowData={sourceData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                autoGroupColumnDef={autoGroupColumnDef}
                domLayout='autoHeight'
                rowSelection={rowSelection}
                onGridReady={onGridReady}
                onRowSelected={onRowSelected}
                onCellClicked={useSelectionColumnClick()}
            />
        </div>
    );
});

export default PrefillSourceAgGrouped;
